import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
// constants
import { useTranslation } from "react-i18next";
// libraries
import { PinInput } from "react-input-pin-code";

import { ALERT } from "../../../pages/home/newtransaction/newtransaction";
// apis
import { updatePinCode, verifyPinCode } from "../../../setup/api/services/user";
// providers and context
import { AuthConfig } from "../../../setup/context/config/authConfig";
import PrimaryButton from "../../common/PrimaryButton";
import Spinner from "../../common/Spinner";
// material ui components
import Alert, { AlertType } from "../../common/WaffyAlert";
import { SECURITY_CODE } from "./constant";
// styles
import styles from "./styles.module.scss";

const PaymentPin = (): JSX.Element => {
  const userId = AuthConfig.getUserInfo()?.id;
  const { t } = useTranslation();
  const [values, setValues] = useState(["", "", "", "", "", ""]);
  const [currentPincode, setCurrentPincode] = useState("");
  const [updatedPincode, setUpdatedPincode] = useState("");
  const [loading, setLoading] = useState<boolean>(false);

  const [pinVerified, setPinVerified] = useState(false);
  const [pinUpdated, setPinUpdated] = useState(false);

  const [alert, setAlert] = useState<ALERT>(ALERT.NONE);
  const [alertText, setAlertText] = useState<string>("");

  const inputStyles = {
    background: "#FFFFFF",
    border: `1px solid #9FA6AB`,
    borderRadius: `8px`,
    fontFamily: "IBM Plex Sans Arabic",
    fontWeight: 700,
    fontSize: "32px",
    color: "#000000",
    height: "60px",
  };

  const handlePinVerified = async (pincode: string) => {
    setCurrentPincode(pincode);
    setLoading(true);
    try {
      const body = { pinCode: pincode };
      const result = await verifyPinCode(userId, body);
      if (result?.data?.valid) {
        setPinVerified(true);
        setValues(["", "", "", "", "", ""]);
        setAlert(ALERT.SUCCESS);
        setAlertText(t("PIN_VERIFIED"));
      } else {
        setPinVerified(false);
        setValues(["", "", "", "", "", ""]);
        setAlert(ALERT.ERROR);
        setAlertText(t("PIN_VERIFICATION_FAILED"));
      }
      setLoading(false);
    } catch (error) {
      Sentry.captureException(error);
      setAlertText(t("PIN_VERIFICATION_FAILED"));
      setPinVerified(false);
      setValues(["", "", "", "", "", ""]);
      setAlert(ALERT.ERROR);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (alert !== ALERT.NONE) {
      setTimeout(() => {
        setAlert(ALERT.NONE);
        setAlertText("");
      }, 2000);
    }
  }, [alert]);

  const handlePinUpdate = async (pincode: string) => {
    setUpdatedPincode(pincode);
    setLoading(true);
    try {
      const body = {
        oldPinCodeOrPassword: currentPincode,
        newPinCode: pincode,
        reNewPinCode: pincode,
      };
      const result = await updatePinCode(userId, body);
      if (result?.data) {
        setAlertText(t("PIN_CHANGED"));
        setAlert(ALERT.SUCCESS);
        setPinUpdated(true);
      } else {
        setPinUpdated(false);
        setAlertText(t("UPDATE_FAILED"));
        setAlert(t("PIN_VERIFIED"));
      }
      setLoading(false);
    } catch (error) {
      Sentry.captureException(error);
      setAlertText(t("UPDATE_FAILED"));
      setAlert(ALERT.ERROR);
      setPinUpdated(false);
      setLoading(false);
    }
  };

  const onVerify = () => {
    handlePinVerified(values.join(""));
  };

  const onEditCode = () => {
    handlePinUpdate(values.join(""));
  };

  return (
    <div dir="rtl" className={styles.payments_container}>
      <>
        {pinUpdated ? (
          <div className={styles.success_container}>
            <img alt="correct" src="/assets/images/svg/tick.svg" />
            <h2>{t("lbl_security_code_successfully_set")}</h2>
          </div>
        ) : (
          <div>
            {!pinVerified ? (
              <h2>{SECURITY_CODE.ENTER_CURRENT_SECURITY_CODE}</h2>
            ) : (
              <h2>{SECURITY_CODE.CHANGE_SECURITY_CODE}</h2>
            )}
            <div dir="ltr" className={styles.pin_container}>
              <PinInput
                name={"securityCode"}
                type="number"
                mask={true}
                values={values}
                autoFocus={true}
                focusBorderColor={"#5BBDFF"}
                validBorderColor={"#E7E9EA"}
                onChange={(value, index, values) => {
                  setValues(values);
                }}
                inputStyle={inputStyles}
              />
            </div>
            <div className={styles.button_wrapper}>
              {!pinVerified ? (
                <PrimaryButton
                  id={`user_profile_verify_pin`}
                  disabled={loading}
                  onClick={onVerify}>
                  {loading ? <Spinner /> : t("lbl_confirm")}
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  id={`user_profile_update_pin`}
                  disabled={loading}
                  onClick={onEditCode}>
                  {loading ? <Spinner /> : t("lbl_login_pin_code_continue_btn")}
                </PrimaryButton>
              )}
            </div>
            {alert !== ALERT.NONE && (
              <Alert
                showIcon
                type={
                  alert === ALERT.SUCCESS ? AlertType.SUCCESS : AlertType.ERROR
                }>
                {alertText}
              </Alert>
            )}
          </div>
        )}
      </>
    </div>
  );
};

export default PaymentPin;
